// Concrete `MenuContext` builder. Wires the per-app dependencies (settings,
// history, recorder, scene host) into the small interface menu screens consume.
// Kept in `scenes/` rather than `ui/` so `main.ts` only constructs the
// dependency graph; the screens themselves remain unaware of any of it.

import { createDiscardModal } from '@bn-games/history-core/ui';
import { ActiveGameStore } from '../history/ActiveGameStore.js';
import {
  replayFromRecord,
  resumeActiveGame as bootResumeActiveGame,
  startNewGame as bootStartNewGame,
  type BootstrapDeps,
} from './Bootstrap.js';
import { t } from '../i18n/I18n.js';
import type { GameRecorder } from '../history/GameRecorder.js';
import type { HistoryStore } from '../history/HistoryStore.js';
import type { SettingsManager } from '../settings/SettingsManager.js';
import type { MenuContext, Scene } from '../ui/types.js';
import type { Settings } from '../settings/types.js';

export interface MenuContextDeps {
  readonly bootstrap: BootstrapDeps;
  readonly gameRecorder: GameRecorder;
  readonly historyStore: HistoryStore;
  readonly settingsManager: SettingsManager;
  readonly navigate: (scene: Scene) => void;
  readonly onSettingsChanged: (before: Settings, after: Settings) => void;
}

export function buildMenuContext(deps: MenuContextDeps): MenuContext {
  return {
    navigate: deps.navigate,
    getSettings: () => deps.settingsManager.get(),
    updateSettings: (partial) => {
      const before = deps.settingsManager.get();
      deps.settingsManager.update(partial);
      deps.onSettingsChanged(before, deps.settingsManager.get());
    },
    startGame: (difficulty) => {
      deps.navigate('game');
      bootStartNewGame(deps.bootstrap, difficulty);
    },
    replayGame: (record) => {
      // Re-attempt clobbers any in-progress session; PlayMenu's discard modal
      // doesn't gate HistoryList rows, so ask here when an active session
      // exists. If the user keeps the active session we skip the launch.
      const launch = (): void => {
        deps.navigate('game');
        replayFromRecord(deps.bootstrap, record);
      };
      confirmDiscardThenRun(deps.gameRecorder, launch);
    },
    getActiveSession: () => deps.gameRecorder.getSession() ?? ActiveGameStore.peek(),
    resumeActiveGame: () => {
      deps.navigate('game');
      bootResumeActiveGame(deps.bootstrap);
    },
    discardActiveSession: (onConfirm) => confirmDiscardThenRun(deps.gameRecorder, onConfirm),
    historyStore: deps.historyStore,
  };
}

function confirmDiscardThenRun(recorder: GameRecorder, onConfirm?: () => void): void {
  const session = recorder.getSession() ?? ActiveGameStore.peek();
  if (session === null) {
    onConfirm?.();
    return;
  }
  // No sudoku-specific discard-modal strings are wired up yet — fall back to
  // the pause-menu "Restart" copy which carries the same semantic ("this will
  // throw away the puzzle you're solving") in both locales. The body is the
  // restart's `pause.title` so both keys exist in en + ru without a new i18n
  // pass landing under task 21's scope.
  createDiscardModal({
    strings: {
      title: t('pause.restart'),
      body: t('pause.title'),
      keep: t('common.cancel'),
      discard: t('pause.restart'),
    },
    onDiscard: () => {
      recorder.discard();
      onConfirm?.();
    },
    dismissible: true,
  });
}
